// styles
import { defineComponent, h, useId, type PropType, type VNode } from 'vue'
import './data-table.scss'

import {
  SbDataTableActions,
  SbDataTableBody,
  SbDataTableBodyRow,
  SbDataTableHeader,
  SbDataTableColumn,
  SbDataTableColumnButton,
} from './components'

import { getPropertyValue, isNumeric } from '../../utils'
import sharedProps from './sharedProps'
import type { DataTableHeader, DataTableItem } from './types'

const SbDataTable = defineComponent({
  name: 'SbDataTable',

  data: () => ({
    selectedRows: [] as DataTableItem[],
    sortKey: undefined as string | undefined,
    sortOrder: undefined as number | undefined,
  }),

  props: {
    ...sharedProps,
    rowIdPrefix: {
      type: String,
      default: 'sb',
    },
    loadingAnnouncement: {
      type: String,
      default: undefined,
    },
    caption: {
      type: String,
      default: undefined,
    },
    rowFieldIdentifier: {
      type: String,
      default: 'name',
    },
    ariaLabelledby: {
      type: String,
      default: undefined,
    },
    ariaDescribedby: {
      type: String,
      default: undefined,
    },
  },

  computed: {
    uid() {
      return useId()
    },

    ariaDescribedBy() {
      const ariaDescribedBy = this.ariaDescribedby ? [this.ariaDescribedby] : []
      if (
        this.loadingAnnouncement ||
        (this.allowSelection && !this.hideActionsMenu)
      ) {
        ariaDescribedBy.push(`${this.uid}-announcements`)
      }
      return ariaDescribedBy.join(',')
    },

    dataTestid() {
      return this.$attrs['data-testid'] || 'sb-data-table'
    },

    allRowsSelected() {
      const selectableItems = this.items.filter(
        (item) => item.selectable !== false,
      )

      return Boolean(
        this.selectedRows.length === selectableItems.length &&
          this.hasSelectedRowsInList.length,
      )
    },

    dataTableContext() {
      return {
        rowFieldIdentifier: this.rowFieldIdentifier,

        // methods to control row selection
        selectRow: this.selectRow,
        deselectRow: this.deselectRow,
        selectAll: this.selectAll,
        deselectAll: this.deselectAll,

        // method to control sorting
        toggleTableOrder: this.toggleTableOrder,
        resetTableOrder: this.resetTableOrder,
      }
    },

    hasSelectedRowsInList() {
      return this.items.filter((item) =>
        this.selectedRows.some(
          (row) => JSON.stringify(item) === JSON.stringify(row),
        ),
      )
    },

    showHeader() {
      return !this.hideHeader && this.headers.length
    },

    sortedData() {
      if (this.sortKey && this.sortOrder !== 0) {
        return this.doSort()
      }
      return this.items
    },

    paginatedItemsList() {
      if (this.pagination === null) {
        return undefined
      }
      let paginatedItems: DataTableItem[][] = []
      const items = [...this.sortedData]
      const pageChunkIndex = this.pagination.currentPage - 1
      while (items.length > 0) {
        const pageParam = Object.keys(this.pagination).includes('pageSize')
          ? 'pageSize'
          : 'perPage'
        const pageChunk = items.splice(0, this.pagination[pageParam])
        paginatedItems = [...paginatedItems, pageChunk]
      }
      return paginatedItems[pageChunkIndex]
    },
  },

  watch: {
    items() {
      if (!this.keepSelectedOnChange) {
        this.deselectAll()
      }
    },
    selectedRows(value) {
      this.$emit('selected-rows', value)
    },
  },

  emits: {
    'emit-action': (value: string) => true,
    cancel: () => true,
    'selected-rows': (value: DataTableItem[]) => true,
  },

  methods: {
    /**
     * method to sort array of items
     */
    doSort() {
      const local = [...this.items]

      return local.sort((a, b) => {
        let val1 = getPropertyValue(a, this.sortKey)
        let val2 = getPropertyValue(b, this.sortKey)

        if (val1 === null || val1 === undefined) val1 = ''
        if (val2 === null || val2 === undefined) val2 = ''

        if (isNumeric(val1) && isNumeric(val2)) {
          return (val1 - val2) * (this.sortOrder ?? 1)
        }

        const str1 = val1.toString()
        const str2 = val2.toString()

        return str1.localeCompare(str2) * (this.sortOrder ?? 1)
      })
    },

    selectRow(row: DataTableItem) {
      const index = this.selectedRows.indexOf(row)
      if (index === -1) {
        this.selectedRows.push(row)

        this.selectedRows = this.items.filter((item) => {
          return this.hasSelectedRowsInList.indexOf(item) > -1
        })
      }
    },

    deselectRow(row: DataTableItem) {
      this.selectedRows = this.selectedRows.filter((selectedRow) => {
        if (JSON.stringify(selectedRow) !== JSON.stringify(row))
          return selectedRow
      })
    },

    /**
     * method to select all body row(s)
     */
    selectAll() {
      this.selectedRows = this.items.filter((item) => item.selectable !== false)
    },

    /**
     * method to deselect all body row(s)
     */
    deselectAll() {
      this.selectedRows = []
    },

    toggleTableOrder(order: number, key: string) {
      this.sortOrder = order
      this.sortKey = key
    },

    resetTableOrder() {
      this.sortOrder = undefined
      this.sortKey = undefined
    },
  },

  provide() {
    return {
      dataTableContext: () => this.dataTableContext,
    }
  },

  render() {
    const renderActions = () => {
      return this.hideActionsMenu
        ? null
        : h(SbDataTableActions, {
            actions: this.actions,
            hideLabelActionsBreakpoint: this.hideLabelActionsBreakpoint,
            selectedRows: this.hasSelectedRowsInList,
            sticky: this.stickyMenu,
            onCancel: () => {
              this.$emit('cancel')
              this.deselectAll()
            },
            onClick: (value) => this.$emit('emit-action', value),
          })
    }

    const renderTable = () => {
      let headerData: DataTableHeader[] = []
      let bodyData: VNode[] = []
      const items = this.paginatedItemsList
        ? [...this.paginatedItemsList]
        : [...this.sortedData]

      if (this.$slots.default && this.$slots.default()) {
        const children = this.$slots.default().filter((e) => e.type.name)
        headerData = children.map((element) => {
          const hasCenteredProp =
            element.props && 'is-content-centered' in element.props
              ? element.props['is-content-centered']
              : false
          const hideLabel =
            element.props && 'hide-label' in element.props
              ? element.props['hide-label']
              : false
          return {
            ...element.props,
            isContentCentered: hasCenteredProp,
            hideLabel: Boolean(hideLabel),
            scopedSlots: element.children,
          }
        })

        bodyData = items.map((tableRow, index) => {
          const columns = children.map((tableData, index) => {
            return h(
              SbDataTableColumn,
              {
                ...tableData.props,
                row: tableRow,
                'data-testid': `${this.dataTestid}-column__${index}`,
              },
              tableData.children,
            )
          })

          return h(
            SbDataTableBodyRow,
            {
              allowSelection: this.allowSelection,
              headers: [...headerData],
              row: tableRow,
              selectedRows: this.hasSelectedRowsInList,
              rowId: `${this.rowIdPrefix}-${index}`,
              'data-testid': `${this.dataTestid}-body-row__${index}`,
            },
            () => [columns],
          )
        })
      }

      return h(
        'table',
        {
          class: 'sb-data-table__container',
          'data-testid': this.dataTestid + '-table',
          'aria-busy': this.isLoading ? 'true' : 'false',
          'aria-hidden': this.isLoading ? 'true' : 'false',
          'aria-describedby': this.ariaDescribedBy,
          'aria-labelledby': this.ariaLabelledby,
        },
        [
          this.caption
            ? h(
                'caption',
                {
                  class: 'screen-reader-only',
                  id: `${this.uid}-caption`,
                },
                this.caption,
              )
            : null,
          this.showHeader
            ? h(SbDataTableHeader, {
                allowSelection: this.allowSelection,
                allRowsSelected: this.allRowsSelected,
                headers: this.headers,
                selectedRows: this.hasSelectedRowsInList,
                sortedKey: this.sortKey,
                isSortIconAlwaysVisible: this.isSortIconAlwaysVisible,
                'data-testid': `${this.dataTestid}-header`,
              })
            : null,
          this.sortedData.length && !this.$slots.default
            ? h(SbDataTableBody, {
                allowSelection: this.allowSelection,
                headers: this.headers,
                items: this.sortedData,
                selectedRows: this.hasSelectedRowsInList,
                'data-testid': `${this.dataTestid}-body`,
              })
            : null,
          this.$slots.default
            ? [
                !this.hideHeader
                  ? h(SbDataTableHeader, {
                      allowSelection: this.allowSelection,
                      allRowsSelected: this.allRowsSelected,
                      headers: [...headerData],
                      selectedRows: this.hasSelectedRowsInList,
                      sortedKey: this.sortKey ?? '',
                      isSortIconAlwaysVisible: this.isSortIconAlwaysVisible,
                      'data-testid': `${this.dataTestid}-header`,
                    })
                  : null,
                h('tbody', bodyData),
              ]
            : null,
        ],
      )
    }

    const renderAnnouncements = () => {
      const actionAnnouncement =
        this.hasSelectedRowsInList.length > 0
          ? h('p', 'Actions menu available on top')
          : undefined

      const loadingAnnouncement = this.loadingAnnouncement
        ? h('p', this.loadingAnnouncement)
        : undefined

      return h(
        'div',
        {
          class: 'screen-reader-only',
          'aria-live': 'polite',
          'data-testid': `${this.dataTestid}-announcements`,
          id: `${this.uid}-announcements`,
        },
        [actionAnnouncement, loadingAnnouncement],
      )
    }

    return h(
      'div',
      {
        class: [
          'sb-data-table',
          {
            'sb-data-table--loading': this.isLoading,
            'sb-data-table--striped': this.striped,
          },
        ],
      },
      [
        this.ariaDescribedBy && renderAnnouncements(),
        this.hasSelectedRowsInList.length > 0 && renderActions(),
        renderTable(),
        this.isLoading,
      ],
    )
  },
})

export { SbDataTable, SbDataTableColumn, SbDataTableColumnButton }
export type {
  DataTableItem,
  DataTableHeader,
  DataTablePagination,
} from './types'
